import { createContext, useContext, useEffect, useState } from "react";

import type { UserClient, UserCluster } from "../../modules/users/types";
import type { Usina, UsinaByClient } from "../../modules/usinas/types";
import { useClientClusterUsinaFilter } from "../hooks/useClientClusterUsinaFilter";
import { MainContext } from "./MainContextProvider";
import {
  CenterScreensEnum,
  type CenterScreenValue,
} from "../../modules/operations/centerScreensEnum";
import { usinasApi } from "../../services/api";

type ClientClusterUsinaFilterContextType = {
    selectedClientFilter: { id: string; name: string } | null;
    setSelectedClientFilter: (client: { id: string; name: string } | null | undefined) => void;

    selectedCluster: { id: string; name: string } | null;
    setSelectedCluster: (cluster: { id: string; name: string } | null | undefined) => void;

    selectedUsinaFilter: { id: string; name: string } | null;
    setSelectedUsinaFilter: (usina: { id: string; name: string } | null | undefined) => void;

    availableClients: UserClient[];
    availableClusters: UserCluster[];
    availableUsinas: UsinaByClient[];
    usinas: Usina[];

    selectedScreen: CenterScreenValue;
    setSelectedScreen: (screen: CenterScreenValue) => void;
}

const ClientClusterUsinaFilterContext = createContext<ClientClusterUsinaFilterContextType | null>(null);

export function useClientClusterUsinaFilterContext() {
    const context = useContext(ClientClusterUsinaFilterContext);
    if (!context) {
        throw new Error("useClientClusterUsinaFilterContext deve ser usado dentro de um ClientClusterUsinaFilterContextProvider");
    }
    return context;
}

type ClientClusterUsinaFilterContextProviderProps = {
    children: React.ReactNode;
}

export function ClientClusterUsinaFilterContextProvider({ children }: ClientClusterUsinaFilterContextProviderProps) {
    const [selectedScreen, setSelectedScreen] = useState<CenterScreenValue>(CenterScreensEnum.Operational);

    const { userDetails: user } = useContext(MainContext);

    const [usinas, setUsinas] = useState<Usina[]>([]);

    useEffect(() => {
        mount();
    }, [user]);

    const mount = async () => {
        try {
            const response = await usinasApi.getUsinas(false);
            const raw = response.data;
            setUsinas(Array.isArray(raw) ? raw : []);
        } catch (error) {
            throw error;
        }
    };

    const clientClusterUsinaFilter = useClientClusterUsinaFilter({
        user,
        usinas,
        selectedScreen,
        setSelectedScreen
    });

    return (
        <ClientClusterUsinaFilterContext.Provider value={{ ...clientClusterUsinaFilter, usinas }}>
            {children}
        </ClientClusterUsinaFilterContext.Provider>
    );
}